//* 면세계산서/수기환불 요청 목록 공용 컨텐츠 — 페이지는 kind만 다른 두 개로 분리 운영.
//* seller: 자기 shop 접수 내역 확인 + 수정·재요청 / admin: 전체 대상 + 행 내 처리(완료·반려).
//* 신규 API 없음: 기존 find에 필터(status $in, 인덱스)·populate만 얹는다. seller find는
//* owningFilter가 서버에서 자동 스코프. 구서버에선 조회 실패/빈 목록 안내로 처리.
//* (스펙: docs/shop-order-seller-request-spec.md)

import { FC, useCallback, useEffect, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { OrderManualRefundRequestModal } from '@service/seller/components/OrderManualRefundRequestModal';
import { OrderTaxInvoiceRequestModal } from '@service/seller/components/OrderTaxInvoiceRequestModal';
import {
  processShopOrderSellerRequest,
  revertShopOrderSellerRequest,
  ShopOrderSellerRequestKind,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderSellerRequestListUrl } from '@shared/api/shopOrder/shopOrder.hook';
import {
  ShopOrderManualRefund,
  ShopOrderRequestUser,
  ShopOrderSellerRequestRow,
  ShopOrderTaxInvoiceRequest,
} from '@shared/api/shopOrder/shopOrder.interface';
import { sellerRequestStatusLabel } from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { Image } from 'antd';

type Kind = ShopOrderSellerRequestKind;
type StatusTab = 'pending' | 'processed';

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const processErrorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 400:
      return '처리할 수 없는 요청 상태입니다.';
    case 403:
      return '관리자만 처리할 수 있습니다.';
    case 404:
      return '주문을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.';
    case 409:
      return '요청 상태가 방금 바뀌었습니다(이미 처리됐을 수 있음). 새로고침 후 확인해주세요.';
    default:
      return '처리에 실패했습니다.';
  }
};

const dateString = (date?: Date) =>
  formatDate(date, { dateSeparater: '.', contains: { hours: true, minutes: true } });

//* 주문 식별용 짧은 날짜 (연도·시각 생략) — 결제일 등
const shortDate = (date?: Date) =>
  formatDate(date, { dateSeparater: '.', contains: { year: false } });

const userNameOf = (user?: ShopOrderRequestUser): string | undefined =>
  typeof user === 'object' ? user?.info?.name : undefined;

const requestOf = (
  row: ShopOrderSellerRequestRow,
  kind: Kind,
): ShopOrderTaxInvoiceRequest | ShopOrderManualRefund | undefined =>
  kind === 'taxInvoiceRequest' ? row.taxInvoiceRequest : row.manualRefund;

//* 모노톤 + 단일 상태 액센트(듀얼톤): 카드 좌측 바 + 상태 점만 색을 쓰고 나머지는 회색조.
const STATUS_ACCENT: Record<
  'Requested' | 'Done' | 'Rejected',
  { bar: string; dot: string }
> = {
  Requested: { bar: 'border-l-amber-400', dot: 'bg-amber-400' },
  Done: { bar: 'border-l-emerald-500', dot: 'bg-emerald-500' },
  Rejected: { bar: 'border-l-rose-400', dot: 'bg-rose-400' },
};

//* ─────────────────────────── 행 카드 ───────────────────────────
interface RowProps {
  row: ShopOrderSellerRequestRow;
  kind: Kind;
  isAdminUser: boolean;
  onChanged: () => void;
}

const RequestRowCard: FC<RowProps> = function RequestRowCard({
  row,
  kind,
  isAdminUser,
  onChanged,
}: RowProps) {
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const request = requestOf(row, kind);

  const modalOpenId = `orderRequestList-${kind}-${row._id}`;
  const setModalOpen = useSetRecoilState(simpleOpenAtomFamily(modalOpenId));

  //* admin 행 내 처리 입력
  const [resultNote, setResultNote] = useState('');
  const [cashReceiptCanceled, setCashReceiptCanceled] = useState(true);
  const [processing, setProcessing] = useState<'Done' | 'Rejected' | null>(null);
  const [reverting, setReverting] = useState(false);

  if (!request) return null;
  const taxRequest = kind === 'taxInvoiceRequest' ? row.taxInvoiceRequest : undefined;
  const refundRequest = kind === 'manualRefund' ? row.manualRefund : undefined;
  const bizRegImage =
    typeof taxRequest?.bizRegImage === 'object' ? taxRequest.bizRegImage : undefined;
  const shopName = typeof row.shop === 'object' ? row.shop?.name : undefined;

  //* 계산서/환불을 확인해서 처리하려면 어떤 주문인지 알아야 한다 — 주문 요약 필드들
  const item = row.orderDetail?.shopItemEmbed;
  const qty = row.orderDetail?.quantity ?? 1;
  const delivery = row.deliveryDetail;
  const bugoDetail = row.bugoDetail;

  const onProcess = async (status: 'Done' | 'Rejected') => {
    if (status === 'Rejected' && !resultNote.trim()) {
      notificateError(notifiacationInstance, '반려 사유를 처리 메모에 입력해주세요.');
      return;
    }
    setProcessing(status);
    try {
      await processShopOrderSellerRequest(row._id, kind, {
        status,
        resultNote: resultNote.trim() || undefined,
        //* 발행 완료(Done)일 때만 현금영수증 취소 플래그 전송. 반려에도 딸려가면
        //* cashReceiptCanceledAt이 찍혀 세무 기록에 허위 "취소" 도장이 남는다.
        ...(kind === 'taxInvoiceRequest' && status === 'Done'
          ? { cashReceiptCanceled }
          : {}),
      });
      notificateSuccess(
        notifiacationInstance,
        status === 'Done' ? '완료 처리했습니다.' : '반려 처리했습니다.',
      );
      onChanged();
    } catch (err) {
      console.error(err);
      notificateError(notifiacationInstance, processErrorMessage(err));
    } finally {
      setProcessing(null);
    }
  };

  //* 실수 처리 되돌리기 — 다시 대기(Requested)로. 서버가 부수효과를 정확히 역전한다:
  //* 계좌환불 Done이 취소확정까지 했던 건(cancelFinalized)이면 주문 취소 복귀 + 포인트 회수.
  //* ⚠️ 이미 나간 취소알림은 회수 불가 — 재처리 시 알림이 한 번 더 나가므로 미리 경고한다.
  const onRevert = async () => {
    const finalizedCancel =
      kind === 'manualRefund' &&
      request.status === 'Done' &&
      row.manualRefund?.cancelFinalized;
    const warn = finalizedCancel
      ? '이 건은 취소가 확정된 상태입니다. 되돌리면 주문이 취소요청으로 복귀하고 환불했던 포인트가 회수됩니다.\n\n※ 이미 고객에게 나간 취소알림은 회수되지 않으며, 다시 완료 처리하면 취소알림이 한 번 더 발송됩니다.\n\n대기 상태로 되돌릴까요?'
      : '이 처리를 취소하고 다시 대기 상태로 되돌릴까요?';
    if (!window.confirm(warn)) return;
    setReverting(true);
    try {
      await revertShopOrderSellerRequest(row._id, kind);
      notificateSuccess(notifiacationInstance, '대기 상태로 되돌렸습니다.');
      onChanged();
    } catch (err) {
      console.error(err);
      notificateError(
        notifiacationInstance,
        statusOf(err) === 400
          ? '되돌릴 수 없는 상태입니다. (이미 대기 상태이거나 아직 서버에 반영되지 않은 기능)'
          : processErrorMessage(err),
      );
    } finally {
      setReverting(false);
    }
  };

  //* 텍스트 옆에 붙는 소형 복사 버튼 — 홈택스/뱅킹앱에 옮겨 적는 값들에만
  const copyChip = (value: string) => (
    <button
      type="button"
      className="shrink-0 rounded border border-gray-200 px-1 text-[11px] leading-4 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      onClick={() => {
        navigator.clipboard.writeText(value);
        notificateSuccess(notifiacationInstance, '복사 완료');
      }}
    >
      복사
    </button>
  );

  const accent = STATUS_ACCENT[request.status];

  return (
    <div
      className={`space-y-2 rounded-lg border border-l-4 border-gray-200 bg-white p-3 text-sm ${accent.bar}`}
    >
      {/* 상단: 상태·상점 + 요청 메타 + 수정·재요청 (한 줄) */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${accent.dot}`} />
          <span className="font-medium text-gray-800">
            {sellerRequestStatusLabel[request.status]}
          </span>
        </span>
        {refundRequest && (
          <span className="text-gray-500">
            {refundRequest.target === 'SellerAdvance' ? '선지급 상환' : '고객계좌환불'}
          </span>
        )}
        {shopName && <span className="text-gray-500">· {shopName}</span>}
        <span className="ml-auto flex items-center gap-2 text-xs text-gray-400">
          <span>
            요청 {dateString(request.requestedAt)}
            {userNameOf(request.requestedBy) && ` · ${userNameOf(request.requestedBy)}`}
          </span>
          {request.status !== 'Done' && (
            <button
              type="button"
              className="font-medium text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-gray-900"
              onClick={() => setModalOpen(true)}
            >
              수정·재요청
            </button>
          )}
        </span>
      </div>

      {/* 주문번호 전체 + 복사 (환불·계산서 대사 시 주문 식별 핵심) */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1">
        <span className="shrink-0 text-xs text-gray-400">주문번호</span>
        <span className="break-all font-mono text-xs text-gray-700">{row._id}</span>
        {copyChip(row._id)}
      </div>

      {/* 화환 상품명 — 헤딩처럼 강조 (어떤 상품 주문인지 한눈에) */}
      <div className="flex items-baseline justify-between gap-2">
        <p className="min-w-0 truncate font-medium text-gray-900">
          {item?.name ?? '-'}
          {qty > 1 && <span className="text-gray-500"> ×{qty}</span>}
        </p>
        <p className="shrink-0 font-semibold text-gray-900">
          {row.paymentDetail.paymentByCash.toLocaleString()}원
        </p>
      </div>

      {/* 주문 정보 — 라벨 정렬 표(줄글 대신 스캔 가능하게), 계산서/환불 확인용 */}
      <div className="grid grid-cols-[2.75rem_1fr] gap-x-2 gap-y-1 text-gray-700">
        <span className="text-gray-400">결제</span>
        <span>
          {row.paymentDetail.method}
          {row.paymentDetail.purchasedAt && (
            <span className="text-gray-500">
              {' '}
              · {shortDate(row.paymentDetail.purchasedAt)}
            </span>
          )}
          {item?.priceRetail != null &&
            item.priceRetail !== row.paymentDetail.paymentByCash && (
              <span className="text-gray-400">
                {' '}
                (판매가 {item.priceRetail.toLocaleString()}원)
              </span>
            )}
        </span>

        {(bugoDetail?.senderPhrase || bugoDetail?.condolencePhrase) && (
          <>
            <span className="text-gray-400">문구</span>
            <span className="min-w-0 break-keep">
              {bugoDetail?.senderPhrase && (
                <span className="block">보내는분 “{bugoDetail.senderPhrase}”</span>
              )}
              {bugoDetail?.condolencePhrase && (
                <span className="block text-gray-500">
                  조의 “{bugoDetail.condolencePhrase}”
                </span>
              )}
            </span>
          </>
        )}

        {delivery?.receiverAddress && (
          <>
            <span className="text-gray-400">배송</span>
            <span className="min-w-0 break-keep">
              <span className="block">{delivery.receiverAddress}</span>
              {(delivery.receiverName || delivery.receiverPhoneNumber) && (
                <span className="block text-gray-500">
                  {delivery.receiverName && `상주 ${delivery.receiverName}`}
                  {delivery.receiverName && delivery.receiverPhoneNumber && ' · '}
                  {delivery.receiverPhoneNumber &&
                    phoneNumberStringFormator(delivery.receiverPhoneNumber)}
                </span>
              )}
            </span>
          </>
        )}

        {(delivery?.senderName || delivery?.senderPhoneNumber) && (
          <>
            <span className="text-gray-400">주문자</span>
            <span className="flex flex-wrap items-center gap-1">
              <span>
                {delivery?.senderName ?? '-'}
                {delivery?.senderPhoneNumber &&
                  ` · ${phoneNumberStringFormator(delivery.senderPhoneNumber)}`}
              </span>
              {delivery?.senderPhoneNumber && copyChip(delivery.senderPhoneNumber)}
            </span>
          </>
        )}
      </div>

      {/* 요청 내용 — 썸네일은 텍스트 우측으로 붙여 세로 공간 절약 */}
      {taxRequest && (
        <div className="flex items-start gap-2 rounded-md bg-gray-50 p-2">
          <div className="min-w-0 flex-1 space-y-0.5">
            {taxRequest.bizNumber && (
              <p className="flex flex-wrap items-center gap-1">
                <span className="font-medium">사업자번호 {taxRequest.bizNumber}</span>
                {copyChip(taxRequest.bizNumber)}
              </p>
            )}
            {taxRequest.email && (
              <p className="flex flex-wrap items-center gap-1">
                <span className="break-all">이메일 {taxRequest.email}</span>
                {copyChip(taxRequest.email)}
              </p>
            )}
            {!taxRequest.bizNumber && !bizRegImage?.url && !taxRequest.email && (
              <p className="text-xs text-gray-500">첨부/입력된 사업자 정보 없음</p>
            )}
            {taxRequest.requestNote && (
              <p className="text-xs text-gray-600">메모: {taxRequest.requestNote}</p>
            )}
          </div>
          {bizRegImage?.url && (
            <Image
              src={fileBucketUrl(bizRegImage.url, 'w200')}
              alt="사업자등록증"
              preview={{ src: fileBucketUrl(bizRegImage.url, 'raw') }}
              width={72}
              className="max-h-24 shrink-0 rounded border object-contain"
            />
          )}
        </div>
      )}
      {refundRequest && (
        <div className="space-y-0.5 rounded-md bg-gray-50 p-2">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <span className="font-medium">
              {refundRequest.account.bank} {refundRequest.account.accountNumber} (
              {refundRequest.account.holderName})
            </span>
            {copyChip(
              `${refundRequest.account.bank} ${refundRequest.account.accountNumber} ${refundRequest.account.holderName}`,
            )}
            <span>
              · <span className="font-bold">{refundRequest.amount.toLocaleString()}원</span>
            </span>
            {refundRequest.amount !== row.paymentDetail.paymentByCash && (
              <span className="text-xs text-gray-500">
                (결제 현금액 {row.paymentDetail.paymentByCash.toLocaleString()}원과 다름)
              </span>
            )}
          </p>
          {refundRequest.requestNote && (
            <p className="text-xs text-gray-600">메모: {refundRequest.requestNote}</p>
          )}
        </div>
      )}

      {/* 처리 결과 (완료/반려) */}
      {request.status !== 'Requested' && (
        <div className="text-xs text-gray-600">
          <p>
            처리 {dateString(request.processedAt)}
            {userNameOf(request.processedBy) && ` · ${userNameOf(request.processedBy)}`}
          </p>
          {taxRequest?.cashReceiptCanceledAt && (
            <p>현금영수증 취소 {dateString(taxRequest.cashReceiptCanceledAt)}</p>
          )}
          {request.resultNote && (
            <p className="text-gray-600">
              {request.status === 'Rejected' ? '반려 사유' : '처리 메모'}:{' '}
              {request.resultNote}
            </p>
          )}
          {/* 실수 처리 되돌리기 (admin) — 다시 대기로 */}
          {isAdminUser && (
            <button
              type="button"
              className="mt-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              disabled={reverting}
              onClick={onRevert}
            >
              {reverting ? '되돌리는 중...' : '↩ 대기로 되돌리기'}
            </button>
          )}
        </div>
      )}

      {/* admin 행 내 처리 — 체크박스·메모·버튼을 한 줄에 (좁으면 자동 줄바꿈) */}
      {isAdminUser && request.status === 'Requested' && (
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 p-2">
          {kind === 'taxInvoiceRequest' && (
            <label className="flex shrink-0 items-center gap-1 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={cashReceiptCanceled}
                onChange={(e) => setCashReceiptCanceled(e.target.checked)}
              />
              현금영수증 취소
            </label>
          )}
          <input
            type="text"
            className="min-w-[160px] flex-1 rounded border border-gray-300 bg-white px-2 py-1"
            placeholder={
              kind === 'taxInvoiceRequest'
                ? '승인번호 / 반려 사유'
                : '이체 메모 / 반려 사유'
            }
            value={resultNote}
            onChange={(e) => setResultNote(e.target.value)}
          />
          <div className="ml-auto flex gap-1.5">
            <Button
              className="border border-gray-300 px-3 text-xs text-gray-600 hover:bg-gray-100"
              disabled={processing !== null}
              onClick={() => onProcess('Rejected')}
            >
              {processing === 'Rejected' ? '처리 중...' : '반려'}
            </Button>
            <Button
              className="filled-gray-800 px-3 text-xs"
              disabled={processing !== null}
              onClick={() => onProcess('Done')}
            >
              {processing === 'Done'
                ? '처리 중...'
                : kind === 'taxInvoiceRequest'
                ? '발행 완료'
                : '이체 완료'}
            </Button>
          </div>
        </div>
      )}

      {kind === 'taxInvoiceRequest' ? (
        <OrderTaxInvoiceRequestModal
          openId={modalOpenId}
          order={row}
          onSuccess={onChanged}
        />
      ) : (
        <OrderManualRefundRequestModal
          openId={modalOpenId}
          order={row}
          onSuccess={onChanged}
        />
      )}
    </div>
  );
};

//* ─────────────────────────── 목록 컨텐츠 ───────────────────────────
interface Props {
  kind: Kind;
}

//* 처리 완료 목록 기간 필터 — 완료 건은 무한히 쌓이므로 기본 최근 1주만.
type ProcessedPeriod = '1week' | '1month' | '3month' | 'all';

const PROCESSED_PERIODS: { key: ProcessedPeriod; label: string; days?: number }[] = [
  { key: '1week', label: '최근 1주', days: 7 },
  { key: '1month', label: '최근 1개월', days: 30 },
  { key: '3month', label: '최근 3개월', days: 90 },
  { key: 'all', label: '전체' },
];

const periodFrom = (period: ProcessedPeriod): Date | undefined => {
  const days = PROCESSED_PERIODS.find((p) => p.key === period)?.days;
  if (!days) return undefined;
  const from = new Date();
  from.setDate(from.getDate() - days);
  return from;
};

export const OrderRequestListContent: FC<Props> = function OrderRequestListContent({
  kind,
}: Props) {
  const { sellerRole } = useAuth();
  const role = sellerRole();
  const isAdminUser = role === LowerRole.Admin;

  const [statusTab, setStatusTab] = useState<StatusTab>('pending');
  const [period, setPeriod] = useState<ProcessedPeriod>('1week');
  const [pendingRows, setPendingRows] = useState<ShopOrderSellerRequestRow[] | null>(
    null,
  );
  const [processedRows, setProcessedRows] = useState<
    ShopOrderSellerRequestRow[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  //* 대기(Requested)는 전량, 완료(Done|Rejected)는 기간 이후만 — 각각 별도 조회
  const loadPending = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api_.get<ShopOrderSellerRequestRow[]>(
        shopOrderSellerRequestListUrl(kind, role, 'pending'),
      );
      setPendingRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setPendingRows([]);
      setError(
        '요청 목록을 불러오지 못했습니다. (서버에 아직 반영되지 않은 기능일 수 있습니다)',
      );
    }
  }, [kind, role]);

  const loadProcessed = useCallback(async () => {
    try {
      const { data } = await api_.get<ShopOrderSellerRequestRow[]>(
        shopOrderSellerRequestListUrl(kind, role, 'processed', periodFrom(period)),
      );
      setProcessedRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setProcessedRows([]);
    }
  }, [kind, role, period]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);
  useEffect(() => {
    loadProcessed();
  }, [loadProcessed]);

  const reload = () => {
    loadPending();
    loadProcessed();
  };

  const visibleRows = statusTab === 'pending' ? pendingRows : processedRows;
  const pendingCount = pendingRows?.length ?? 0;
  const processedCount = processedRows?.length ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium leading-6 text-gray-900">
          {kind === 'taxInvoiceRequest' ? '면세계산서 요청' : '계좌환불 요청'}
        </h2>
        <Button className="border border-gray-300 text-sm" onClick={reload}>
          새로고침
        </Button>
      </div>
      <p className="break-keep text-sm text-gray-600">
        {kind === 'taxInvoiceRequest'
          ? '가상계좌·무통장입금 결제 건의 현금영수증 취소 + 면세계산서 발행 요청 목록입니다.'
          : '가상계좌·무통장입금 결제 건의 계좌환불(수기 이체) 요청 목록입니다.'}
        {isAdminUser
          ? ' 대기 건은 행에서 바로 완료·반려 처리할 수 있습니다.'
          : ' 접수한 요청의 처리 현황을 확인하고, 반려 건은 수정해 다시 요청할 수 있습니다.'}
      </p>

      <div className="flex flex-wrap items-center gap-1.5">
        {(
          [
            ['pending', `처리 대기 (${pendingCount})`],
            ['processed', `처리 완료 (${processedCount})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`rounded-full border px-3 py-1 text-sm ${
              statusTab === key
                ? 'border-gray-800 bg-gray-800 font-medium text-white'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setStatusTab(key)}
          >
            {label}
          </button>
        ))}
        {/* 완료 탭에서만 기간 필터 — 완료 건은 무한히 쌓이므로 기본 최근 1주 */}
        {statusTab === 'processed' && (
          <div className="ml-2 flex flex-wrap items-center gap-1 border-l pl-2">
            {PROCESSED_PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`rounded-full border px-2.5 py-0.5 text-xs ${
                  period === p.key
                    ? 'border-gray-700 bg-gray-700 text-white'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <p className="break-keep text-sm text-red-600">{error}</p>}

      {visibleRows === null ? (
        <Loading />
      ) : visibleRows.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">
          {statusTab === 'pending'
            ? '처리 대기 중인 요청이 없습니다.'
            : `해당 기간(${
                PROCESSED_PERIODS.find((p) => p.key === period)?.label
              })에 처리된 요청이 없습니다.`}
        </p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
          {visibleRows.map((row) => (
            <RequestRowCard
              key={row._id}
              row={row}
              kind={kind}
              isAdminUser={isAdminUser}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
};
